import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '@/services/apiClient';
import { Cart } from '@/api/cart';

export interface OfflineSale {
  id: string; // idempotency key
  businessId: string;
  cart: Cart;
  paymentMethod: 'cash' | 'mpesa';
  amount: number;
  customerPhone?: string;
  notes?: string;
  status: 'pending' | 'syncing' | 'failed' | 'completed';
  createdAt: string;
  errorMessage?: string;
}

interface OfflineSyncState {
  queue: OfflineSale[];
  isSyncing: boolean;
  addSaleToQueue: (sale: Omit<OfflineSale, 'id' | 'status' | 'createdAt'>) => void;
  removeSale: (id: string) => void;
  syncQueue: () => Promise<void>;
  clearQueue: () => void;
}

export const useOfflineSyncStore = create<OfflineSyncState>()(
  persist(
    (set, get) => ({
      queue: [],
      isSyncing: false,

      addSaleToQueue: (saleData) => {
        const newSale: OfflineSale = {
          ...saleData,
          id: crypto.randomUUID(),
          status: 'pending',
          createdAt: new Date().toISOString(),
        };
        
        set((state) => ({
          queue: [...state.queue, newSale]
        }));
        
        // Attempt to sync immediately if online
        if (navigator.onLine) {
          get().syncQueue();
        }
      },

      removeSale: (id) => {
        set((state) => ({
          queue: state.queue.filter(s => s.id !== id)
        }));
      },

      clearQueue: () => {
        set({ queue: [] });
      },

      syncQueue: async () => {
        const state = get();
        if (state.isSyncing) return;
        
        const pendingSales = state.queue.filter(s => s.status === 'pending' || s.status === 'failed');
        if (pendingSales.length === 0) return;

        set({ isSyncing: true });

        const updatedQueue = [...state.queue];

        for (const sale of pendingSales) {
          // Map local cart items to direct-sale API payload
          const directSalePayload = {
            payment_method: sale.paymentMethod,
            customer_phone: sale.customerPhone || null,
            discount: 0,
            notes: sale.notes || '',
            offline_metadata: {
              offline_id: sale.id,
              created_at: sale.createdAt,
            },
            items: sale.cart.cart_items.map(item => {
              const product = (item as any).product || (item as any).products;
              return {
                product_id: item.product_id,
                barcode: product?.barcode || product?.manufacturer_barcode || null,
                name: product?.name || 'Unknown Product',
                quantity: item.quantity,
                unit_price: product?.price || 0,
                total_price: (product?.price || 0) * item.quantity,
              };
            })
          };

          try {
            // Update status to syncing
            const saleIndex = updatedQueue.findIndex(s => s.id === sale.id);
            if (saleIndex !== -1) updatedQueue[saleIndex] = { ...sale, status: 'syncing' };
            set({ queue: updatedQueue });

            await apiClient.post(
              `/cart/${sale.businessId}/direct-sale`, 
              directSalePayload
            );

            // If success, remove it from the queue entirely to save local storage space
            const successIndex = updatedQueue.findIndex(s => s.id === sale.id);
            if (successIndex !== -1) {
              updatedQueue.splice(successIndex, 1);
            }
            
          } catch (error: any) {
            console.error(`Failed to sync offline sale ${sale.id}:`, error);
            const failIndex = updatedQueue.findIndex(s => s.id === sale.id);
            if (failIndex !== -1) {
              updatedQueue[failIndex] = { 
                ...sale, 
                status: 'failed',
                errorMessage: error.response?.data?.error || error.message || 'Sync failed'
              };
            }
          }
        }

        set({ queue: updatedQueue, isSyncing: false });
      }
    }),
    {
      name: 'offline-sync-queue',
    }
  )
);

// Auto-sync when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useOfflineSyncStore.getState().syncQueue();
  });
}
